import { useEffect, useRef } from "react";

/**
 * Run `callback` immediately, then on a fixed interval — but PAUSE the interval
 * whenever the browser tab is hidden (Page Visibility API). When the tab
 * becomes visible again, `callback` runs once immediately and the interval
 * resumes.
 *
 * Why this exists:
 *   Users leave the CRM open in a background tab for hours. Notifications
 *   poll every 30 s, Chat heartbeat every 8 s, Followups presence every 10 s,
 *   the meetings clock ticker every 15 s, etc. In a 12-hour background tab
 *   this is thousands of pointless network round-trips plus the corresponding
 *   `setState` calls, each of which allocates JS objects Chrome has to
 *   garbage-collect. Pausing them while the tab is invisible eliminates 95%+
 *   of that memory churn — the "Aw, Snap! Out of Memory" crash on
 *   nodesupport.in was almost entirely driven by these background polls.
 *
 * The callback ref is stable so the interval is created ONCE on mount and
 * torn down on unmount, regardless of how often `callback` or `intervalMs`
 * change on the calling component (no closure/interval churn).
 *
 * @param {() => void | Promise<void>} callback  what to run each tick
 * @param {number} intervalMs                    time between ticks in ms
 * @param {boolean} [enabled=true]               allow the caller to disable
 */
export function useVisibilityPoll(callback, intervalMs, enabled = true) {
  const cbRef = useRef(callback);
  useEffect(() => { cbRef.current = callback; }, [callback]);

  useEffect(() => {
    if (!enabled || !intervalMs) return undefined;
    let timer = null;
    const start = () => {
      if (timer) return;
      // Run immediately when we (re-)start so users see fresh data on tab focus.
      try { cbRef.current?.(); } catch { /* swallow */ }
      timer = setInterval(() => {
        try { cbRef.current?.(); } catch { /* swallow */ }
      }, intervalMs);
    };
    const stop = () => {
      if (timer) { clearInterval(timer); timer = null; }
    };
    const onVis = () => { if (document.hidden) stop(); else start(); };

    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [intervalMs, enabled]);
}
